// Lightweight D-pad focus manager for TV.
// Each focusable element is a <button> or element with tabIndex=0 and class "tv-focusable".
// Arrow keys move spatially; Enter activates; Back/Esc triggers onBack (if any).

import { useEffect } from 'react';

const SELECTOR = '.tv-focusable:not([data-disabled="true"])';

function rect(el) {
  const r = el.getBoundingClientRect();
  return {
    left: r.left,
    right: r.right,
    top: r.top,
    bottom: r.bottom,
    cx: r.left + r.width / 2,
    cy: r.top + r.height / 2,
    w: r.width,
    h: r.height,
  };
}

function pickNextElement(currentEl, dir) {
  const all = Array.from(document.querySelectorAll(SELECTOR)).filter(
    (el) => el !== currentEl && el.offsetParent !== null
  );
  if (all.length === 0) return null;
  const c = rect(currentEl);

  let best = null;
  let bestScore = Infinity;

  for (const el of all) {
    const r = rect(el);
    let inDir = false;
    let primaryDist = 0;
    let crossDist = 0;

    switch (dir) {
      case 'right':
        inDir = r.left >= c.right - 4;
        primaryDist = r.left - c.right;
        crossDist = Math.abs(r.cy - c.cy);
        break;
      case 'left':
        inDir = r.right <= c.left + 4;
        primaryDist = c.left - r.right;
        crossDist = Math.abs(r.cy - c.cy);
        break;
      case 'down':
        inDir = r.top >= c.bottom - 4;
        primaryDist = r.top - c.bottom;
        crossDist = Math.abs(r.cx - c.cx);
        break;
      case 'up':
        inDir = r.bottom <= c.top + 4;
        primaryDist = c.top - r.bottom;
        crossDist = Math.abs(r.cx - c.cx);
        break;
      default:
        break;
    }
    if (!inDir) continue;
    // Weight: perpendicular distance matters a lot
    const score = primaryDist + crossDist * 2.5;
    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  }
  return best;
}

function moveFocus(dir) {
  const active = document.activeElement;
  let current = active && active.classList && active.classList.contains('tv-focusable') ? active : null;
  if (!current) {
    const first = document.querySelector(SELECTOR);
    if (first) first.focus();
    return;
  }
  const next = pickNextElement(current, dir);
  if (next) {
    next.focus();
    next.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }
}

export function useDpadNavigation(onBack) {
  useEffect(() => {
    const handler = (e) => {
      const key = e.key;
      if (key === 'ArrowRight') { e.preventDefault(); moveFocus('right'); }
      else if (key === 'ArrowLeft') { e.preventDefault(); moveFocus('left'); }
      else if (key === 'ArrowDown') { e.preventDefault(); moveFocus('down'); }
      else if (key === 'ArrowUp') { e.preventDefault(); moveFocus('up'); }
      else if (key === 'Backspace' || key === 'Escape' || key === 'GoBack') {
        if (onBack) { e.preventDefault(); onBack(); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onBack]);
}

export function focusFirst() {
  setTimeout(() => {
    const first = document.querySelector(SELECTOR);
    if (first) first.focus();
  }, 100);
}
